import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const BANKS = [
  "Air Bank",
  "Česká spořitelna",
  "ČSOB / Hypoteční banka",
  "Fio banka",
  "Komerční banka",
  "mBank",
  "MONETA Money Bank",
  "Raiffeisenbank",
  "UniCredit Bank",
  "Jiná / nevím",
];

const BENEFITS = [
  "Ozvu se vám zhruba 6 měsíců před koncem fixace",
  "Porovnání sazeb napříč bankami — zůstat, vyjednat, nebo refinancovat",
  "Nezávazně — rozhodnutí je vždycky vaše",
];

export const Fixace = () => {
  const mountedAt = useRef(Date.now());
  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);
  const [form, setForm] = useState({ name: "", email: "", phone: "", fixation_end: "", bank: "", note: "", website: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | sent | demo
  const [serverError, setServerError] = useState("");

  const set = (key) => (e) => {
    setForm({ ...form, [key]: e.target.value });
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError("");
    const next = {};
    if (!form.fixation_end) next.fixation_end = "Vyberte prosím datum.";
    else if (form.fixation_end <= new Date().toISOString().slice(0, 10)) next.fixation_end = "Zadejte prosím budoucí datum.";
    if (form.name.trim().length < 2) next.name = "Napište prosím své jméno.";
    if (!EMAIL_RE.test(form.email.trim())) next.email = "Zadejte prosím platnou e-mailovou adresu.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setStatus("sending");
    try {
      const res = await fetch(`${API}/fixace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, elapsed_ms: Date.now() - mountedAt.current }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && (data.status === "ok" || data.status === "demo")) {
        setStatus("sent");
      } else {
        setStatus("idle");
        const detail = data.detail;
        setServerError(
          (typeof detail === "string" && detail) || "Poptávku se nepodařilo odeslat. Zkuste to prosím znovu."
        );
      }
    } catch {
      setStatus("idle");
      setServerError("Poptávku se nepodařilo odeslat. Zkontrolujte prosím připojení a zkuste to znovu.");
    }
  };

  const inputCls = (err) =>
    `bg-white border ${err ? "border-red-700" : "border-[#E5E5E5]"} focus:border-[#B69A58] outline-none py-3 px-4 w-full text-base transition-colors duration-300 rounded-none placeholder-[#A3A3A3]`;

  const labelCls = "block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2";

  return (
    <section id="fixace" data-testid="fixace-section" className="py-24 md:py-32 border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-12 lg:gap-20">
        <Reveal className="flex flex-col">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Hlídání fixace hypotéky
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-6">
            Končí vám fixace? Ozvu se dřív, než banka
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-6">
            Po skončení fixace vám banka sama nabídne novou sazbu — ale nemusí být
            nejlepší, co je na trhu. Nechte si termín pohlídat: včas se ozvu
            a společně porovnáme, jestli se vyplatí zůstat, vyjednat lepší
            podmínky, nebo refinancovat jinam.
          </p>

          <ul className="mt-8 space-y-4">
            {BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-center gap-4">
                <Check className="h-5 w-5 text-[#B69A58] shrink-0" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-base text-[#252525]">{benefit}</span>
              </li>
            ))}
          </ul>

          <div className="mt-10">
            <img
              src="/images/fixace.webp"
              alt="Model domu a klíče — konec fixace hypotéky"
              data-testid="fixace-image"
              className="w-full h-auto block rounded-tl-[80px] rounded-2xl"
              width={1050}
              height={600}
              loading="lazy"
              decoding="async"
            />
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-[#F0F0F0] bg-white p-8 md:p-10">
            <h3 className="font-display text-2xl text-[#252525]" data-testid="fx-form-title">
              Pohlídat konec mojí fixace
            </h3>

            {status === "sent" ? (
              <div role="status" data-testid="fx-success" className="flex items-start gap-4 mt-8 border border-[#F0F0F0] bg-[#FAFAF8] p-6">
                <Check className="h-6 w-6 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                <p className="text-base text-[#252525] leading-relaxed">
                  Děkuji! Konec vaší fixace mám zaznamenaný. Ozvu se vám s předstihem
                  a společně porovnáme možnosti.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate data-testid="fx-form" className="mt-8 space-y-6">
                <div>
                  <label htmlFor="fx-date" className={labelCls}>
                    Konec fixace *
                  </label>
                  <input
                    id="fx-date"
                    data-testid="fx-date-input"
                    type="date"
                    min={tomorrow}
                    value={form.fixation_end}
                    onChange={set("fixation_end")}
                    className={inputCls(errors.fixation_end)}
                    aria-invalid={!!errors.fixation_end}
                  />
                  <p className="text-xs text-[#737373] mt-2">
                    Najdete ve smlouvě o úvěru nebo v internetovém bankovnictví.
                  </p>
                  {errors.fixation_end && <p role="alert" data-testid="fx-date-error" className="text-sm text-red-700 mt-2">{errors.fixation_end}</p>}
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fx-name" className={labelCls}>Jméno *</label>
                    <input
                      id="fx-name"
                      data-testid="fx-name-input"
                      type="text"
                      autoComplete="name"
                      value={form.name}
                      onChange={set("name")}
                      className={inputCls(errors.name)}
                      aria-invalid={!!errors.name}
                    />
                    {errors.name && <p role="alert" data-testid="fx-name-error" className="text-sm text-red-700 mt-2">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="fx-email" className={labelCls}>E-mail *</label>
                    <input
                      id="fx-email"
                      data-testid="fx-email-input"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set("email")}
                      className={inputCls(errors.email)}
                      aria-invalid={!!errors.email}
                    />
                    {errors.email && <p role="alert" data-testid="fx-email-error" className="text-sm text-red-700 mt-2">{errors.email}</p>}
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fx-phone" className={labelCls}>
                      Telefon <span className="normal-case font-normal">(nepovinné — pro rychlejší kontakt)</span>
                    </label>
                    <input
                      id="fx-phone"
                      data-testid="fx-phone-input"
                      type="tel"
                      autoComplete="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls(false)}
                    />
                  </div>
                  <div>
                    <label htmlFor="fx-bank" className={labelCls}>
                      Současná banka <span className="normal-case font-normal">(nepovinné)</span>
                    </label>
                    <select
                      id="fx-bank"
                      data-testid="fx-bank-select"
                      value={form.bank}
                      onChange={set("bank")}
                      className={`${inputCls(false)} ${form.bank ? "text-[#252525]" : "text-[#A3A3A3]"}`}
                    >
                      <option value="">Vyberte…</option>
                      {BANKS.map((bank) => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="fx-note" className={labelCls}>
                    Poznámka <span className="normal-case font-normal">(nepovinné)</span>
                  </label>
                  <textarea
                    id="fx-note"
                    data-testid="fx-note-input"
                    rows={3}
                    value={form.note}
                    onChange={set("note")}
                    placeholder="Např. zbývající dluh, jestli plánujete změnu bydlení…"
                    className={`${inputCls(false)} resize-y`}
                  />
                </div>

                <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="fx-website">Webové stránky</label>
                  <input
                    id="fx-website"
                    data-testid="fx-honeypot"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set("website")}
                  />
                </div>

                {serverError && <p role="alert" data-testid="fx-server-error" className="text-sm text-red-700">{serverError}</p>}

                <button
                  type="submit"
                  data-testid="fx-submit-button"
                  disabled={status === "sending"}
                  className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-widest font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Odesílám…" : "Pohlídat konec fixace"}
                </button>

                <p className="text-xs text-[#737373] leading-relaxed">
                  Hlídání je nezávazné a nic ve vaší smlouvě nemění.
                  Odesláním souhlasíte se zpracováním uvedených údajů pro vyřízení
                  poptávky a následné kontaktování ve zvoleném termínu.{" "}
                  <Link to="/ochrana-osobnich-udaju" data-testid="fx-privacy-link" className="underline underline-offset-2 hover:text-[#B69A58] transition-colors duration-300">
                    Informace o zpracování osobních údajů
                  </Link>
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
